import { useEffect, useMemo, useState } from "react";
import { Card, PaginadorTabla, Stack } from "@/components/ui";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import type { TipoDocumentoAdicional } from "@/lib/types";
import { useTiposDocumentoAdmin } from "../hooks/use-tipos-documento-admin";
import { listarTiposDocumentoAdmin } from "../services/tipos-documento-service";
import { TiposDocumentoHeader } from "./TiposDocumentoHeader";
import { TipoDocumentoCrearForm } from "./TipoDocumentoCrearForm";
import { TiposDocumentoTable } from "./TiposDocumentoTable";

const PAGE_SIZE = 15;

/**
 * Página de administración del catálogo de tipos de documento adicional (P31).
 * La búsqueda y el paginado se resuelven en el SERVIDOR (query params con
 * debounce de 350ms) · el loader del route precarga la primera página.
 */
export function AdminTiposDocumentoPage() {
  const [modoCrear, setModoCrear] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [page, setPage] = useState(1);
  const [busquedaDebounced] = useDebouncedValue(busqueda, 350);

  const filtrosServer = useMemo(
    () => (busquedaDebounced.trim() ? { busqueda: busquedaDebounced.trim() } : {}),
    [busquedaDebounced]
  );

  useEffect(() => {
    setPage(1);
  }, [busquedaDebounced]);

  const { data } = useTiposDocumentoAdmin({ ...filtrosServer, page, pageSize: PAGE_SIZE });
  const tipos = data?.items ?? [];
  const total = data?.total ?? 0;

  const obtenerTodos = async (): Promise<TipoDocumentoAdicional[]> => {
    const todos: TipoDocumentoAdicional[] = [];
    for (let p = 1; ; p++) {
      const pagina = await listarTiposDocumentoAdmin({ ...filtrosServer, page: p, pageSize: 100 });
      todos.push(...pagina.items);
      if (todos.length >= pagina.total || pagina.items.length === 0) break;
    }
    return todos;
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <TiposDocumentoHeader
        total={data?.totalCatalogo ?? 0}
        activos={data?.totalActivos ?? 0}
        modoCrear={modoCrear}
        onIniciarCrear={() => setModoCrear(true)}
      />
      <Stack gap="md">
        {modoCrear ? <TipoDocumentoCrearForm onSalir={() => setModoCrear(false)} /> : null}
        <Card padding="lg">
          <Stack gap="md">
            <PaginadorTabla total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} variante="superior" />
            <TiposDocumentoTable
              tipos={tipos}
              busqueda={busqueda}
              onBusquedaChange={setBusqueda}
              sinResultados={total === 0}
              hayBusqueda={busquedaDebounced.trim().length > 0}
              obtenerFilas={obtenerTodos}
            />
            <PaginadorTabla total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} sustantivo="tipos" />
          </Stack>
        </Card>
      </Stack>
    </div>
  );
}
